import type { Analytics } from './analytics'
import type { Api } from './api'
import { CLOSE_ICON, RESTART_ICON, SEND_ICON, heart } from './icons'
import type { Bootstrap, ChatLink, Crisis, FlowNode } from './types'

/**
 * The chat panel: menu navigation, message log, free-text input and the rating.
 *
 * Two rules run through all of it.
 *
 * **Navigating the menu never calls the API.** Every option, response and link comes from
 * the bootstrap snapshot already in memory, so the guided path is instant and costs nothing.
 * Only free text can reach the server.
 *
 * **Links are only ever resolved by ID** against `flow.links`. Nothing that arrives as text
 * becomes a link, which is what stops a wrong address reaching a visitor.
 */
export class Panel {
  private readonly el: HTMLElement
  private readonly log: HTMLElement
  private readonly breadcrumb: HTMLElement
  private readonly composer: HTMLElement
  private readonly input: HTMLInputElement
  private readonly sendButton: HTMLButtonElement
  private readonly live: HTMLElement

  /** Node keys from the root to where the visitor is now. */
  private path: FlowNode[] = []

  /**
   * How many messages the transcript held when each level of `path` was entered, so Back can
   * put the transcript back rather than printing the level again.
   */
  private marks: number[] = []
  private awaiting = false
  private rated = false

  /** Whether a rating was actually submitted, as opposed to merely asked for. */
  private ratingGiven = false

  constructor(
    private readonly boot: Bootstrap,
    private readonly api: Api,
    private readonly analytics: Analytics,
    private readonly onClose: () => void,
  ) {
    const s = boot.settings

    this.el = document.createElement('div')
    this.el.className = 'panel'
    this.el.hidden = true
    this.el.setAttribute('role', 'dialog')
    this.el.setAttribute('aria-modal', 'true')
    this.el.setAttribute('aria-label', s.panel_title)

    this.el.innerHTML = `
      <div class="header">
        ${heart('#ffffff', s.colour_gold)}
        <h2>${escape(s.panel_title)}</h2>
        <button class="icon-button restart" type="button" title="${escape(s.restart_label)}">
          <span class="sr">${escape(s.restart_label)}</span>${RESTART_ICON}
        </button>
        <button class="icon-button close" type="button" title="Close">
          <span class="sr">Close chat</span>${CLOSE_ICON}
        </button>
      </div>
      <div class="breadcrumb" hidden>
        <button class="back" type="button">← Back</button>
      </div>
      <div class="log" role="log" aria-live="polite" aria-relevant="additions"></div>
      <form class="composer">
        <label class="sr" for="gvfb-input">${escape(s.freetext_placeholder)}</label>
        <input id="gvfb-input" type="text" autocomplete="off"
               placeholder="${escape(s.freetext_placeholder)}" maxlength="500">
        <button class="send" type="submit" disabled>
          <span class="sr">Send</span>${SEND_ICON}
        </button>
      </form>
      <div class="footer">
        Automated assistant.
        ${s.privacy_url ? `<a href="${escape(s.privacy_url)}" target="_blank" rel="noopener">Privacy</a>` : ''}
      </div>
      <div class="sr" aria-live="assertive"></div>
    `

    this.log = this.el.querySelector('.log') as HTMLElement
    this.breadcrumb = this.el.querySelector('.breadcrumb') as HTMLElement
    this.composer = this.el.querySelector('.composer') as HTMLElement
    this.input = this.el.querySelector('input') as HTMLInputElement
    this.sendButton = this.el.querySelector('.send') as HTMLButtonElement
    this.live = this.el.querySelector('.sr[aria-live="assertive"]') as HTMLElement

    if (!s.ai_enabled) {
      // Without the AI there is nothing to type at, so don't offer a dead input.
      this.composer.hidden = true
    }

    this.wire()
  }

  element(): HTMLElement {
    return this.el
  }

  private wire(): void {
    this.el.querySelector('.close')?.addEventListener('click', () => this.onClose())
    this.el.querySelector('.restart')?.addEventListener('click', () => this.restart())
    this.el.querySelector('.back')?.addEventListener('click', () => this.back())

    this.input.addEventListener('input', () => {
      this.sendButton.disabled = this.input.value.trim().length < 2 || this.awaiting
    })

    this.composer.addEventListener('submit', (event) => {
      event.preventDefault()
      void this.submit()
    })

    this.el.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') {
        this.trapFocus(event)
      }
    })

    // Escape is bound on the document, not the panel. Bound to the panel it misses the case
    // where focus has left the shadow tree — which happens the moment a focused control
    // disables itself, such as the send button after a question is submitted.
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.el.hidden) {
        event.preventDefault()
        this.onClose()
      }
    })
  }

  /**
   * Keeps Tab cycling within the panel. Without this, a keyboard visitor tabs out of an
   * open dialog into the page behind it and has no way back.
   */
  private trapFocus(event: KeyboardEvent): void {
    const focusable = [
      ...this.el.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((node) => node.offsetParent !== null)

    if (focusable.length === 0) {
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = this.el.shadowRoot?.activeElement ?? document.activeElement

    if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    } else if (event.shiftKey && active === first) {
      event.preventDefault()
      last.focus()
    }
  }

  /** Called when the launcher opens the panel. */
  start(): void {
    if (this.log.childElementCount === 0) {
      this.bot(this.boot.settings.intro_message, { notice: true })
      this.showMenu()
    }

    // Start at the top. The intro says this is an automated assistant and gives the phone
    // number for a real person — auto-scrolling to the newest message would push that
    // straight out of view, which is exactly the wrong thing to hide.
    this.log.scrollTop = 0

    // Focus the first option rather than the input: most visitors want the menu, and it
    // announces the panel's purpose to a screen reader immediately.
    window.setTimeout(() => {
      const target = this.el.querySelector<HTMLElement>('.chip') ?? this.input

      target.focus({ preventScroll: true })
      this.log.scrollTop = 0
    }, 60)
  }

  private restart(): void {
    this.path = []
    this.marks = []
    this.rated = false
    this.ratingGiven = false
    this.log.innerHTML = ''
    this.updateBackBar()
    this.bot(this.boot.settings.intro_message, { notice: true })
    this.showMenu()
    this.announce('Chat restarted.')
  }

  /**
   * Going back rewinds the transcript to the moment before the last choice was made.
   *
   * It used to re-render the level it was returning to, which appended the parent's prompt,
   * body and options to the bottom of the log — so Back read as the assistant repeating
   * itself, and pressing it twice printed the same passage twice. Nothing moved backwards.
   *
   * A chat log is a record of what happened, so the only honest way back is to remove what
   * the abandoned step added. The options for the level being returned to are still sitting
   * in the log, disabled from when they were used, so they are switched back on rather than
   * drawn again.
   */
  private back(): void {
    this.path.pop()
    this.rewind(this.marks.pop() ?? 0)
    this.updateBackBar()

    const level = this.path[this.path.length - 1]

    this.announce(level ? `Back to ${level.label}.` : 'Back to the main options.')
  }

  /**
   * Drop every message added after `length`, then hand the visitor back the choices at that
   * point.
   */
  private rewind(length: number): void {
    while (this.log.childElementCount > length) {
      this.log.lastElementChild?.remove()
    }

    const chips = [...this.log.querySelectorAll<HTMLElement>('.chips')].pop()

    for (const chip of chips?.querySelectorAll('button') ?? []) {
      chip.disabled = false
    }

    // The rating is asked once per conversation. If rewinding took the question away before
    // it was answered, it has not been asked yet.
    if (!this.ratingGiven && this.log.querySelector('.stars') === null) {
      this.rated = false
    }

    this.log.scrollTop = this.log.scrollHeight
  }

  private currentPath(): string {
    return this.path.map((node) => node.key).join(' > ')
  }

  /**
   * The bar carries the Back button only. It used to spell out the full trail as well, but
   * a few levels deep that wrapped onto a second line and pushed the answer out of view —
   * on a panel this small the visitor's own last click is better context than a path they
   * have to read.
   */
  private updateBackBar(): void {
    this.breadcrumb.hidden = this.path.length === 0
  }

  // ------------------------------------------------------------------ menu

  private showMenu(): void {
    this.bot(this.boot.settings.menu_prompt)
    this.chips(
      this.boot.flow.nodes.map((node) => ({
        label: `${node.emoji ?? ''} ${node.label}`.trim(),
        onClick: () => this.choose(node),
      })),
    )
  }

  private choose(node: FlowNode): void {
    // Taken before anything is appended, so Back has a point to rewind to. The options row
    // being used is already in the log at this point and is deliberately kept.
    this.marks.push(this.log.childElementCount)
    this.path.push(node)
    this.updateBackBar()

    this.analytics.track({
      type: 'option_click',
      tracking_code: node.tracking_code,
      node_key: node.key,
      label: node.label,
      path: this.currentPath(),
    })

    this.user(`${node.emoji ?? ''} ${node.label}`.trim())
    this.openNode(node)
  }

  private openNode(node: FlowNode, options: { silent?: boolean } = {}): void {
    if (node.prompt && node.children.length > 0) {
      this.bot(node.prompt)
    }

    if (node.body) {
      this.bot(node.body)
    }

    this.renderLinks(node.link_ids)

    if (node.children.length > 0) {
      this.chips(
        node.children.map((child) => ({
          label: `${child.emoji ?? ''} ${child.label}`.trim(),
          onClick: () => this.choose(child),
        })),
      )

      return
    }

    // A leaf: offer the way back to a person, then ask for the rating.
    this.humanOffer()

    if (!options.silent) {
      this.askRating()
    }
  }

  /**
   * Renders links by looking each ID up in the published registry. An ID that isn't there is
   * skipped rather than guessed at — this is the last line of the allow-list.
   */
  private renderLinks(ids: number[]): void {
    const links = ids
      .map((id) => this.boot.flow.links[String(id)])
      .filter((link): link is ChatLink => Boolean(link))

    if (links.length === 0) {
      return
    }

    const wrap = document.createElement('div')
    wrap.className = 'msg bot links'

    for (const link of links) {
      const anchor = document.createElement('a')
      anchor.className = 'chip link'
      anchor.href = link.url
      anchor.textContent = this.linkLabel(link)

      if (link.kind === 'page' || link.kind === 'form') {
        anchor.target = '_blank'
        anchor.rel = 'noopener noreferrer'
      }

      anchor.addEventListener('click', () => {
        this.analytics.track({
          type: 'link_click',
          tracking_code: link.tracking_code,
          link_id: link.id,
          label: link.label,
          path: this.currentPath(),
          meta: { is_donation: link.is_donation },
        })
      })

      wrap.append(anchor)
    }

    this.append(wrap)
  }

  private linkLabel(link: ChatLink): string {
    if (link.kind === 'email') {
      return `✉ ${link.label}`
    }

    if (link.kind === 'phone') {
      return `☎ ${link.label}`
    }

    return `${link.label} →`
  }

  private humanOffer(): void {
    this.chips([
      {
        label: this.boot.settings.human_label,
        onClick: () => {
          const contact = this.boot.flow.nodes.find((node) => node.key === 'contact-us')

          if (contact) {
            this.choose(contact)
          }
        },
      },
    ])
  }

  // ---------------------------------------------------------------- free text

  private async submit(): Promise<void> {
    const question = this.input.value.trim()

    if (question.length < 2 || this.awaiting) {
      return
    }

    this.input.value = ''
    this.sendButton.disabled = true
    this.awaiting = true

    this.user(question)
    this.analytics.track({ type: 'freetext', path: this.currentPath() })

    const typing = this.showTyping()

    const response = await this.api.ask(question)

    typing.remove()
    this.awaiting = false

    // Return focus to the input. Without this, focus is on a control that just disabled
    // itself, which drops it to the document body and strands a keyboard visitor.
    this.input.focus()

    if (!response) {
      this.bot(this.boot.settings.fallback_message, { notice: true })
      this.announce('No answer available.')

      return
    }

    if (response.crisis) {
      this.renderCrisis(response.crisis)
      this.analytics.track({ type: 'crisis_shown' })
    }

    this.bot(response.answer, { notice: !response.answered })
    this.renderLinks(response.links.map((link) => link.id))

    const suggestions = response.suggestions
      .map((key) => this.findNode(key))
      .filter((node): node is FlowNode => Boolean(node))

    if (suggestions.length > 0) {
      this.chips(
        suggestions.map((node) => ({
          label: `${node.emoji ?? ''} ${node.label}`.trim(),
          onClick: () => this.choose(node),
        })),
      )
    }

    this.announce('New reply.')
    this.askRating()
  }

  private renderCrisis(crisis: Crisis): void {
    const wrap = document.createElement('div')
    wrap.className = 'msg bot'

    const card = document.createElement('div')
    card.className = 'crisis'

    const message = document.createElement('p')
    message.textContent = crisis.message
    card.append(message)

    for (const resource of crisis.resources) {
      const anchor = document.createElement('a')
      anchor.href = resource.url
      anchor.textContent = resource.label
      card.append(anchor)
    }

    wrap.append(card)
    this.append(wrap)
  }

  private findNode(key: string, nodes = this.boot.flow.nodes): FlowNode | null {
    for (const node of nodes) {
      if (node.key === key) {
        return node
      }

      const found = this.findNode(key, node.children)

      if (found) {
        return found
      }
    }

    return null
  }

  // ------------------------------------------------------------------ rating

  private askRating(): void {
    if (this.rated) {
      return
    }

    this.rated = true

    this.bot(this.boot.settings.rating_prompt)

    const wrap = document.createElement('div')
    wrap.className = 'msg bot'

    const stars = document.createElement('div')
    stars.className = 'stars'
    stars.setAttribute('role', 'group')
    stars.setAttribute('aria-label', this.boot.settings.rating_prompt)

    for (let value = 1; value <= 5; value++) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'star'
      button.textContent = '☆'
      button.setAttribute('aria-label', `${value} out of 5`)

      button.addEventListener('click', () => {
        const path = this.currentPath()

        void this.api.rate(value, path)
        this.analytics.track({ type: 'rating', label: String(value), path })
        this.ratingGiven = true

        // Fill in the chosen star and everything below it, then freeze the control.
        for (const [index, node] of [...stars.children].entries()) {
          node.textContent = index < value ? '★' : '☆'
          node.setAttribute('aria-pressed', String(index < value))
          ;(node as HTMLButtonElement).disabled = true
        }

        this.bot('Thank you — that helps us improve.', { notice: true })
        this.announce('Thank you for your rating.')
      })

      stars.append(button)
    }

    wrap.append(stars)
    this.append(wrap)
  }

  // -------------------------------------------------------------- rendering

  private bot(text: string, options: { notice?: boolean } = {}): void {
    if (!text) {
      return
    }

    const wrap = document.createElement('div')
    wrap.className = 'msg bot'

    const bubble = document.createElement('div')
    bubble.className = options.notice ? 'notice' : 'bubble'
    // textContent, never innerHTML: content is staff-authored or model-generated, and
    // neither should be able to inject markup into the page.
    bubble.textContent = text

    wrap.append(bubble)
    this.append(wrap)
  }

  private user(text: string): void {
    const wrap = document.createElement('div')
    wrap.className = 'msg user'

    const bubble = document.createElement('div')
    bubble.className = 'bubble'
    bubble.textContent = text

    wrap.append(bubble)
    this.append(wrap)
  }

  private chips(items: { label: string; onClick: () => void }[]): void {
    const wrap = document.createElement('div')
    wrap.className = 'msg bot chips'

    for (const item of items) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'chip'
      button.textContent = item.label

      button.addEventListener('click', () => {
        // Disabled, not removed. Leaving them live lets someone re-enter a branch they
        // already left, but taking the row out of the log loses the record of what was
        // offered — and leaves Back nothing to re-enable, which is what made it re-render
        // the level and repeat itself.
        for (const sibling of wrap.querySelectorAll('button')) {
          sibling.disabled = true
        }

        item.onClick()
      })

      wrap.append(button)
    }

    this.append(wrap)
  }

  private showTyping(): HTMLElement {
    const wrap = document.createElement('div')
    wrap.className = 'msg bot'
    wrap.innerHTML = '<div class="typing"><span></span><span></span><span></span></div>'
    wrap.setAttribute('aria-label', 'Thinking')

    this.append(wrap)

    return wrap
  }

  private append(node: HTMLElement): void {
    this.log.append(node)
    this.log.scrollTop = this.log.scrollHeight
  }

  /** For messages that need announcing immediately rather than with the polite log. */
  private announce(message: string): void {
    this.live.textContent = message
  }
}

function escape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
